import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { API_URL } from '../../config/api';
import './Management.css';

const ServiceManagement = () => {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [existingImageIds, setExistingImageIds] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });

  useEffect(() => {
    fetchServices();
  }, [token]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/services?populate=*`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Kan diensten niet laden');
      const data = await response.json();
      console.log('📋 Services Fetched:', data);
      console.log('Services data:', data.data);
      setServices(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const normalizeOrientation = (file) =>
    new Promise((resolve) => {
      createImageBitmap(file).then((bitmap) => {
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        bitmap.close();
        canvas.toBlob(
          (blob) => resolve(new File([blob], file.name, { type: blob.type })),
          file.type || 'image/jpeg',
          0.92
        );
      });
    });

  const uploadImage = async (file) => {
    const normalized = await normalizeOrientation(file);
    const data = new FormData();
    data.append('files', normalized);
    const response = await fetch(`${API_URL}/api/upload`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: data,
    });
    if (!response.ok) throw new Error('Afbeelding uploaden mislukt');
    const result = await response.json();
    return result[0].id;
  };

  const resetForm = () => {
    setFormData({ title: '', description: '' });
    setImageFile(null);
    setImagePreview(null);
    setExistingImageIds([]);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = editingId ? 'PUT' : 'POST';
      const endpoint = editingId
        ? `${API_URL}/api/services/${editingId}`
        : `${API_URL}/api/services`;

      const descriptionBlocks = formData.description
        ? [
            {
              type: 'paragraph',
              children: [{ type: 'text', text: formData.description }],
            },
          ]
        : [];

      let imageIds = [...existingImageIds];
      if (imageFile) {
        const newId = await uploadImage(imageFile);
        imageIds = [newId];
      }

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          data: {
            title: formData.title,
            description: descriptionBlocks,
            image: imageIds,
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ Save Error:', errorData);
        throw new Error(errorData.message || 'Kan dienst niet opslaan');
      }
      await fetchServices();
      resetForm();
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Weet je zeker dat je deze dienst wilt verwijderen?')) return;

    try {
      const response = await fetch(`${API_URL}/api/services/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Kan dienst niet verwijderen');
      await fetchServices();
    } catch (err) {
      setError(err.message);
    }
  };

  const getDescriptionText = (desc) => {
    if (!desc) return '';
    if (typeof desc === 'string') return desc;
    if (Array.isArray(desc)) {
      // Handle Strapi blocks format
      return desc
        .map(block => {
          if (block.children && Array.isArray(block.children)) {
            return block.children.map(child => child.text || '').join(' ');
          }
          return '';
        })
        .join(' ');
    }
    return '';
  };

  const handleEdit = (service) => {
    const attrs = service.attributes || service;
    const images = attrs.image?.data || attrs.image || [];
    const ids = Array.isArray(images) ? images.map(img => img.id) : [];
    const firstUrl = Array.isArray(images) && images[0]
      ? `${API_URL}${images[0].attributes?.url || images[0].url}`
      : null;

    setFormData({
      title: attrs.title || '',
      description: getDescriptionText(attrs.description),
    });
    setExistingImageIds(ids);
    setImagePreview(firstUrl);
    setImageFile(null);
    setEditingId(service.id);
    setShowForm(true);
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Diensten</h1>
        <button className="btn-primary" onClick={() => showForm ? resetForm() : setShowForm(true)}>
          {showForm ? 'Annuleren' : '+ Dienst Toevoegen'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form className="admin-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Dienstnaam</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Beschrijving</label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              rows="4"
            />
          </div>
          <div className="form-group">
            <label>Afbeelding</label>
            {imagePreview && (
              <div style={{ marginBottom: '8px' }}>
                <img
                  src={imagePreview}
                  alt="Voorbeeldweergave"
                  style={{ maxHeight: '120px', maxWidth: '200px', borderRadius: '4px', objectFit: 'cover' }}
                />
                <button
                  type="button"
                  style={{ display: 'block', marginTop: '4px', fontSize: '12px', color: '#e53e3e', background: 'none', border: 'none', cursor: 'pointer' }}
                  onClick={() => { setImageFile(null); setImagePreview(null); setExistingImageIds([]); }}
                >
                  Afbeelding verwijderen
                </button>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setImageFile(file);
                  setImagePreview(URL.createObjectURL(file));
                }
              }}
            />
          </div>
          <button type="submit" className="btn-submit">
            {editingId ? 'Dienst Bijwerken' : 'Dienst Aanmaken'}
          </button>
        </form>
      )}

      {loading ? (
        <p className="loading">Diensten worden geladen...</p>
      ) : services.length === 0 ? (
        <p className="empty">Geen diensten gevonden</p>
      ) : (
        <div className="items-table">
          <table>
            <thead>
              <tr>
                <th>Afbeelding</th>
                <th>Naam</th>
                <th>Beschrijving</th>
                <th>Acties</th>
              </tr>
            </thead>
            <tbody>
              {services.map((service) => {
                const attrs = service.attributes || service;
                const title = attrs.title || '';
                const description = attrs.description || '';
                const descText = getDescriptionText(description).substring(0, 50) || '-';
                const images = attrs.image?.data || attrs.image || [];
                const thumbUrl = Array.isArray(images) && images[0]
                  ? `${API_URL}${images[0].attributes?.url || images[0].url}`
                  : null;

                return (
                  <tr key={service.id}>
                    <td>
                      {thumbUrl
                        ? <img src={thumbUrl} alt={title} style={{ width: '60px', height: '40px', objectFit: 'cover', borderRadius: '4px' }} />
                        : <span style={{ color: '#aaa', fontSize: '12px' }}>Geen</span>
                      }
                    </td>
                    <td>{title}</td>
                    <td>{descText}{descText.length > 50 ? '...' : ''}</td>
                    <td className="actions">
                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(service)}
                      >
                        Bewerken
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(service.id)}
                      >
                        Verwijderen
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ServiceManagement;
